import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLicense } from "./licenseContext";
import { useAppState } from "./appStateContext";
import apiClient from "../api/apiClient";
import { filterStepsForUser } from "../utils/tutorialPermissions";

const TutorialContext = createContext();

const STORAGE_KEY_STATUS = "tutorial_status";
const STORAGE_KEY_STEP = "tutorial_last_step";
const STORAGE_KEY_PHASE = "tutorial_phase";

export function TutorialProvider({ children, permissions }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentLicense } = useLicense();
    const { userProfile } = useAppState();
    
    const [active, setActive] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [filteredSteps, setFilteredSteps] = useState([]);
    const [tutorialStatus, setTutorialStatus] = useState(null);
    const [currentPhase, setCurrentPhase] = useState(1);
    const [singleStepMode, setSingleStepMode] = useState(false);
    const [sectionOnlyId, setSectionOnlyId] = useState(null);
    const [overrideSteps, setOverrideSteps] = useState(null);
    const [gettingStartedWatched, setGettingStartedWatched] = useState(true);
    const [productsWatched, setProductsWatched] = useState(true);

    const statusFetched = useRef(false);
    const gettingStartedTriggered = useRef(false);
    const productsTriggered = useRef(false);

    const title = currentLicense?.title;
    const isLocationScoped = currentLicense?.location_scoped === 1;

    useEffect(() => {
        const steps = filterStepsForUser(permissions, title, currentPhase, isLocationScoped);
        setFilteredSteps(steps);
    }, [permissions, title, currentPhase, isLocationScoped]);

    useEffect(() => {
        if (userProfile !== "user" || statusFetched.current) return;
        statusFetched.current = true;

        apiClient
            .get("/tutorial/status")
            .then((res) => {
                const status = res.data.status;
                const phase = res.data.currentPhase || 1;
                setTutorialStatus(status);
                setCurrentPhase(phase);
                setGettingStartedWatched(!!res.data.gettingStartedWatched);
                setProductsWatched(!!res.data.productsWatched);
                localStorage.setItem(STORAGE_KEY_STATUS, status);
                localStorage.setItem(STORAGE_KEY_PHASE, String(phase));
                if (status === "in_progress" && res.data.lastStepId) {
                    localStorage.setItem(STORAGE_KEY_STEP, res.data.lastStepId);
                }
            })
            .catch(() => {});
    }, [userProfile]);

    // Auto-trigger Getting Started on login if not watched
    useEffect(() => {
        if (gettingStartedWatched) return;
        if (gettingStartedTriggered.current) return;
        if (filteredSteps.length === 0) return;
        if (active) return;
        if (currentPhase !== 1) return;

        gettingStartedTriggered.current = true;
        setCurrentIndex(0);
        setActive(true);
        localStorage.setItem(STORAGE_KEY_STATUS, "in_progress");
        setTutorialStatus("in_progress");
    }, [gettingStartedWatched, filteredSteps.length, active, currentPhase]);

    const activeSteps = overrideSteps || filteredSteps;
    const currentStep = activeSteps[currentIndex] || null;
    const totalSteps = activeSteps.length;
    const progress = totalSteps > 0 ? currentIndex / (totalSteps - 1) : 0;

    const saveProgress = useCallback(
        (stepId) => {
            localStorage.setItem(STORAGE_KEY_STEP, stepId);
            localStorage.setItem(STORAGE_KEY_STATUS, "in_progress");
            localStorage.setItem(STORAGE_KEY_PHASE, String(currentPhase));
            apiClient.put("/tutorial/progress", { lastStepId: stepId, phase: currentPhase }).catch(() => {});
        },
        [currentPhase]
    );

    const start = useCallback((phase) => {
        const targetPhase = phase || currentPhase;
        if (targetPhase !== currentPhase) {
            setCurrentPhase(targetPhase);
        }
        setSingleStepMode(false);
        setSectionOnlyId(null);
        setCurrentIndex(0);
        setActive(true);
    }, [currentPhase]);

    const startAtStep = useCallback(
        (stepId) => {
            const allSteps = [
                ...filterStepsForUser(permissions, title, 1, isLocationScoped),
                ...filterStepsForUser(permissions, title, 2, isLocationScoped),
            ];
            const step = allSteps.find((s) => s.id === stepId);
            if (!step) return;
            setSingleStepMode(true);
            setOverrideSteps([step]);
            setCurrentIndex(0);
            setActive(true);
        },
        [permissions, title, isLocationScoped]
    );

    const jumpToSection = useCallback(
        (sectionId) => {
            const allSteps = [
                ...filterStepsForUser(permissions, title, 1, isLocationScoped),
                ...filterStepsForUser(permissions, title, 2, isLocationScoped),
            ];
            const sectionSteps = allSteps.filter((s) => s.section === sectionId);
            if (sectionSteps.length === 0) return;
            setSingleStepMode(false);
            setSectionOnlyId(sectionId);
            setOverrideSteps(sectionSteps);
            setCurrentIndex(0);
            setActive(true);
        },
        [permissions, title, isLocationScoped]
    );

    const next = useCallback(() => {
        if (singleStepMode) {
            setActive(false);
            setSingleStepMode(false);
            setOverrideSteps(null);
            return;
        }

        if (overrideSteps) {
            const nextOverrideIdx = currentIndex + 1;
            if (nextOverrideIdx >= overrideSteps.length) {
                setActive(false);
                setSectionOnlyId(null);
                setOverrideSteps(null);
                return;
            }
            setCurrentIndex(nextOverrideIdx);
            return;
        }

        const nextIdx = currentIndex + 1;
        if (nextIdx >= filteredSteps.length) {
            setActive(false);
            if (currentPhase === 1) {
                localStorage.setItem(STORAGE_KEY_STATUS, "not_started");
                localStorage.setItem(STORAGE_KEY_PHASE, "2");
                setCurrentPhase(2);
                setTutorialStatus("not_started");
                setGettingStartedWatched(true);
                apiClient.put("/tutorial/complete-phase", { phase: 1 }).catch(() => {});
            } else {
                localStorage.setItem(STORAGE_KEY_STATUS, "completed");
                setTutorialStatus("completed");
                setProductsWatched(true);
                apiClient.put("/tutorial/complete").catch(() => {});
            }
            return;
        }
        setCurrentIndex(nextIdx);
        saveProgress(filteredSteps[nextIdx].id);
    }, [currentIndex, filteredSteps, overrideSteps, saveProgress, singleStepMode, currentPhase]);

    const back = useCallback(() => {
        if (singleStepMode) return;
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex, singleStepMode]);

    const skip = useCallback(() => {
        setActive(false);
        setSingleStepMode(false);
        setSectionOnlyId(null);
        if (overrideSteps) {
            setOverrideSteps(null);
            return;
        }
        if (currentPhase === 1) {
            localStorage.setItem(STORAGE_KEY_STATUS, "not_started");
            localStorage.setItem(STORAGE_KEY_PHASE, "2");
            setCurrentPhase(2);
            setTutorialStatus("not_started");
            setGettingStartedWatched(true);
            apiClient.put("/tutorial/skip", { phase: 1 }).catch(() => {});
        } else {
            localStorage.setItem(STORAGE_KEY_STATUS, "skipped");
            setTutorialStatus("skipped");
            setProductsWatched(true);
            apiClient.put("/tutorial/skip", { phase: 2 }).catch(() => {});
        }
    }, [currentPhase, overrideSteps]);

    const dismiss = useCallback(() => {
        setActive(false);
        setSingleStepMode(false);
        setSectionOnlyId(null);
        setOverrideSteps(null);
    }, []);

    const reset = useCallback(async () => {
        try {
            await apiClient.put("/tutorial/reset");
            localStorage.setItem(STORAGE_KEY_STATUS, "not_started");
            localStorage.setItem(STORAGE_KEY_PHASE, "1");
            localStorage.removeItem(STORAGE_KEY_STEP);
            setTutorialStatus("not_started");
            setCurrentPhase(1);
            setCurrentIndex(0);
            setGettingStartedWatched(false);
            setProductsWatched(false);
            gettingStartedTriggered.current = false;
            productsTriggered.current = false;
        } catch {}
    }, []);

    const checkProductsTutorial = useCallback(() => {
        if (productsTriggered.current) return;
        if (productsWatched) return;
        if (currentPhase !== 2) return;

        productsTriggered.current = true;
        const steps = filterStepsForUser(permissions, title, 2, isLocationScoped);
        if (steps.length === 0) return;
        setFilteredSteps(steps);
        setCurrentIndex(0);
        setActive(true);
        localStorage.setItem(STORAGE_KEY_STATUS, "in_progress");
        setTutorialStatus("in_progress");
    }, [productsWatched, currentPhase, permissions, title, isLocationScoped]);

    const routeHandledRef = useRef(null);

    useEffect(() => {
        routeHandledRef.current = null;
    }, [currentIndex]);

    useEffect(() => {
        if (!active || !currentStep) return;
        if (routeHandledRef.current === currentStep.id) return;
        if (currentStep.route && currentStep.route !== location.pathname) {
            routeHandledRef.current = currentStep.id;
            navigate(currentStep.route);
        }
    }, [active, currentStep, location.pathname, navigate]);

    const value = {
        active,
        currentStep,
        currentIndex,
        totalSteps,
        progress,
        filteredSteps,
        tutorialStatus,
        currentPhase,
        singleStepMode,
        permissions,
        isLocationScoped,
        gettingStartedWatched,
        productsWatched,
        start,
        startAtStep,
        next,
        back,
        skip,
        dismiss,
        jumpToSection,
        reset,
        checkProductsTutorial,
    };

    return (
        <TutorialContext.Provider value={value}>
            {children}
        </TutorialContext.Provider>
    );
}

export function useTutorial() {
    const ctx = useContext(TutorialContext);
    if (!ctx) throw new Error("useTutorial must be used within TutorialProvider");
    return ctx;
}
